import { useContext, useEffect, useMemo } from 'react';
import { Box, LinearProgress } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { isEmpty, isNil } from 'lodash';
import { useParams } from 'react-router-dom';
import { VALID_PAGE_TYPES_TO_EXPORT } from 'common/constants/pageTypes';
import {
  EQUITY_VALUE_CARD_TITLE,
  LIVE_VALUE_CARDS,
  UNREALIZED_FIRM_TOTAL_CARD_TITLE,
} from 'common/constants/valuations';
import { useStore } from 'common/store';
import { Currency } from 'common/types/format';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import { ActionButtons, Breadcrumbs, ExtraActionsMenu, HorizontalNavigation, ValueCard } from 'components';
import { CapIQStatusCard } from 'components/CapIQStatusCard';
import { DEFAULT_CURRENCY, REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import LayoutContext from 'context/LayoutContext';
import { ExcelExportDialogV2 as ExcelExportDialog } from 'layouts/Main/components/ExcelExportDialogV2';
import { useGetAllocationValuesByCompany } from 'services/hooks/companies';
import { useAllocationValuesStore } from 'store';
import { getBooleanValue, getNumberValue, getStringValue } from 'utilities';
import { Title } from './components';
import { ProgressBar, StyledAppBar, StyledBoxContainer, StyledToolbar, TitleContainer } from './styles';
import { CompanyParams, HeaderProps, ValueCardsProps } from './types';
import BulkActionsDrawer from '../../layouts/Main/components/BulkActionsLedger/BulkActionsDrawer';


const ValueCards = (props: ValueCardsProps) => {
  const { company, measurementDate } = props;
  const { tableSlugParam } = useParams<CompanyParams>();

  const shouldRenderValueCards = useMemo(
    () => LIVE_VALUE_CARDS.includes(tableSlugParam) && !isEmpty(company) && !isEmpty(measurementDate),
    [company, measurementDate, tableSlugParam]
  );

  const currentEquityValue = useAllocationValuesStore(state => state.currentEquityValue);
  const currentFirmTotal = useAllocationValuesStore(state => state.currentFirmTotal);

  const companyCurrency = useMemo(
    () =>
      ({
        code: company?.financials_currency ?? DEFAULT_CURRENCY,
        units: REGULAR_UNIT,
      }) as Currency,
    [company]
  );
  const isLoadingLiveValues = useMemo(
    () => isNil(currentEquityValue) || isNil(currentFirmTotal),
    [currentEquityValue, currentFirmTotal]
  );

  const equityValue = useMemo(() => getNumberValue(currentEquityValue), [currentEquityValue]);

  const firmTotal = useMemo(() => getNumberValue(currentFirmTotal), [currentFirmTotal]);

  if (!shouldRenderValueCards) {
    return null;
  }

  // Display Header Cards on Companies pages
  return (
    <>
      {/* Cap IQ Health */}
      <CapIQStatusCard />
      {/* Equity Value */}
      <ValueCard
        currency={companyCurrency}
        isLoading={isLoadingLiveValues}
        title={EQUITY_VALUE_CARD_TITLE}
        value={equityValue}
      />
      {/* Firm Total */}
      <ValueCard
        currency={companyCurrency}
        isLoading={isLoadingLiveValues}
        title={UNREALIZED_FIRM_TOTAL_CARD_TITLE}
        value={firmTotal}
      />
    </>
  );
};

const Header = (props: HeaderProps) => {
  const { children } = props;
  const {
    currentMeasurementDate,
    extraPageActions,
    navItems,
    openExportDialog,
    openBulkActionsDialog,
    toggleBulkActionsDialog,
    pageActions,
    pageBreadcrumbs,
    pageTitle,
    pageType,
    toggleExportDialog,
  } = useContext(LayoutContext) as unknown as LayoutContextValues;

  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo, isShowLoadingProgress } = storeValue;

  const setCurrentAllocationId = useAllocationValuesStore(state => state.setCurrentAllocationId);
  const currentAllocationVersion = useAllocationValuesStore(state => state.currentAllocationVersion);

  const isValidCompanyMeasurementDate = useMemo(
    () => currentMeasurementDate?.company_id === companyInfo?.id,
    [companyInfo, currentMeasurementDate]
  );

  const { data: allocationValues } = useGetAllocationValuesByCompany({
    company: companyInfo,
    measurementDate: currentMeasurementDate,
    version: currentAllocationVersion,
    shouldQueryAutomatically: !!isValidCompanyMeasurementDate,
  });

  useEffect(() => {
    if (!isNil(allocationValues?.id)) {
      setCurrentAllocationId?.(allocationValues?.id);
    }
  }, [allocationValues, setCurrentAllocationId]);

  return (
    <>
      <StyledAppBar color="inherit" elevation={1} position="fixed">
        <StyledToolbar>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            {/* Title and Breadcrumbs */}
            <TitleContainer>
              {/* Title */}
              {pageTitle ? <Title pageTitle={pageTitle} /> : <Skeleton height={50} variant="text" width={550} />}
              {/* BreadCrumbs */}
              {pageBreadcrumbs && <Breadcrumbs />}
            </TitleContainer>

            {/* Values Cards and Filters */}
            <Box>
              <StyledBoxContainer>
                <ValueCards company={companyInfo} measurementDate={currentMeasurementDate} />
                {/* Filters */}
                {children}
              </StyledBoxContainer>
            </Box>
          </Box>
        </StyledToolbar>

        {/* Navigation */}
        {!isEmpty(navItems) && (
          <HorizontalNavigation>
            <>
              {pageActions && <ActionButtons />}
              {extraPageActions && <ExtraActionsMenu />}
            </>
          </HorizontalNavigation>
        )}
      </StyledAppBar>

      {/* Loading Progress Bar */}
      {!isEmpty(navItems) && <ProgressBar>{isShowLoadingProgress && <LinearProgress color="primary" />}</ProgressBar>}

      {/* Excel Export */}
      {VALID_PAGE_TYPES_TO_EXPORT.includes(getStringValue(pageType)) && (
        <ExcelExportDialog onClose={toggleExportDialog as () => void} open={getBooleanValue(openExportDialog)} />
      )}
      <BulkActionsDrawer open={openBulkActionsDialog} onClose={toggleBulkActionsDialog} />
    </>
  );
};

export default Header;
